import { EVENTS } from './events'
import { getRemarkPreset } from './Bot.remarks'
import { call, fuid } from '@/utils/navigation/misc'

const INITIAL_STATE = {
	icon: 'burocratik',
	message: 'We are Büro',
}

function createBot() {
	const stateStack = [{ id: 0, ...INITIAL_STATE }]
	const events = new Map()
	let component = {}

	function registerComponent(componentContext) {
		component = componentContext
	}

	function get() {
		return stateStack[stateStack.length - 1]
	}

	function parseStatePayload(payload = {}) {
		let { message, icon } = payload
		const remark = payload.preset && getRemarkPreset(payload.preset)

		if (remark) {
			message = remark.message
			icon = remark.icon
		}

		if (Array.isArray(message)) {
			message = message[Math.floor(Math.random() * message.length)]
		}

		return {
			message: message ?? get().message,
			icon: icon ?? get().icon,
		}
	}

	function set(payload, options) {
		const id = fuid()
		const state = parseStatePayload(payload)

		stateStack.push({ id, ...state })
		call('onUpdate', component, get(), options)

		return (_options) => unset(id, _options)
	}

	function unset(id, options) {
		if (!id) {
			console.error("[Bot] Cannot unset state without state 'id'")
			return
		}

		const index = stateStack.findIndex((entry) => entry.id === id)

		if (index > 0) {
			stateStack.splice(index, 1)
			call('onUpdate', component, get(), options)
		}
	}

	function setupEvent(payload) {
		try {
			const { type } = payload

			if (!type || !EVENTS.has(type)) {
				throw new Error(`[Bot.events] Invalid event type: '${type}'`)
			}

			if (!payload.message && !payload.icon && !payload.preset) {
				throw new Error(`[Bot.events] Event setup must include a message, icon, or preset`)
			}

			const id = fuid()
			const event = EVENTS.get(type)(id, payload, {
				id,
				bot: { get, set },
				commit: (options) =>
					set(
						{
							message: payload.message,
							icon: payload.icon,
							preset: payload.preset,
						},
						options,
					),
				remove: () => removeEvent(id, 'self'),
			})

			if (event === false) return false

			if (!event || typeof event.destroy !== 'function') {
				throw new Error(`[Bot.events] Unable to setup '${payload.type}' event`)
			}

			events.set(id, event)
			return event
		} catch (error) {
			console.error(error)
			return false
		}
	}

	function getEvent(id) {
		return id === 'all' ? [...events.values()] : events.get(id)
	}

	function removeEvent(id, origin) {
		const selectedEvents = getEvent(id)

		;[selectedEvents].flat().forEach((event) => {
			if (!event) return

			if (origin !== 'self' && typeof event.destroy === 'function') {
				event.destroy()
			}

			events.delete(event.id)
		})
	}

	return {
		registerComponent,
		get,
		set,
		events: {
			setup: setupEvent,
			get: getEvent,
			remove: removeEvent,
		},
		get component() {
			return component
		},
	}
}

export const BotInstance = createBot()

export function botPlugin() {
	return {
		provide: {
			bot: BotInstance,
		},
	}
}
